// Castintech dashboard — entry point.
// Faz 9b PR10 (final) — bridge.js'i absorb eder + dashboard'un yapis fonksiyonlari
// (navigate, render, fetchAll, refresh, guardAuth) burada toplandi.
//
// Module import grafi (16 modul):
// - state, config, utils → temel
// - api, sse → ust katman
// - views/{pano, ekip, canli, onaylar, skills, telegram, saglik}
// - ui/{sidebar, topbar, toast, suggestions, drawer, detail-panel, keyboard}
//
// Faz 14 PR-A→F: Object.assign(window, ...) tamamen silindi. Tum cross-module
// callback'ler direct import. Tek expose: state + Api (devtools convenience).
//
// CRITICAL ordering: type="module" defer-by-default → DOMContentLoaded'tan once
// tum import'lar resolve oldu → bootstrap async olarak baslayabilir.

import { state } from './state.js';
import * as Api from './api.js';
import { compareTasksStable } from './utils.js';

// Named imports (main.ts içindeki navigate/render/refresh/fetchAll'tan kullanım).
import { loadHealth, loadAudit, startSaglikTimer, stopSaglikTimer, renderHealth, renderAudit, bindSaglikStatic, bindAuditFeedDelegation } from './views/saglik.js';
import { renderKanban, renderAssigneeFilter, bindNewIssueModalStatic, bindPanoStatic, bindKanbanDelegation } from './views/pano.js';
import { renderSkills, loadMeta, loadSkillUsage, bindSkillsDelegation } from './views/skills.js';
import { renderTelegram, loadTelegramSessions, bindTelegramSendStatic, bindTelegramRefresh, bindTelegramGridDelegation } from './views/telegram.js';
import { renderEkip, renderAccountsCard, renderFallbackCard, fetchAccounts, bindEkipDelegation } from './views/ekip.js';
import { renderCanli } from './views/canli.js';
import { renderOnaylar, bindOnaylarDelegation } from './views/onaylar.js';
import { renderBeniBekleyenler } from './views/beni-bekleyenler.js';
import { loadMusteriler, renderMusteriler } from './views/musteriler.js';
import { loadMulkler, renderMulkler } from './views/mulkler.js';
import { loadIsler, renderIsler } from './views/isler.js';
import { startHealthSummaryTimer } from './views/health-summary.js';
import { loadFaturalar, renderFaturalar } from './views/faturalar.js';
import { loadKararlar, renderKararlar } from './views/kararlar.js';
import { loadRuntimeHealth, renderRuntimeHealth, startRuntimeHealthTimer, stopRuntimeHealthTimer, bindRuntimeHealthRefresh } from './views/runtime-health.js';
// Faz 30 — 6 yeni operasyon paneli
import { loadDeployHistory, renderDeployHistory } from './views/deploy-history.js';
// Faz 40 PR-A — Pilot Deploy Status (post-deploy izleme paneli, Pilot SSH yapmaz)
import { loadPilotDeployStatus, renderPilotDeployStatus } from './views/pilot-deploy-status.js';
// Faz 52-Y W3c — hotfix-approval / activation-history / auto-block-history
// view'ları silindi (ölü ekranlar; W3a'da nav'dan kalkmıştı). Backend
// endpoint'leri Telegram akışları için duruyor.
import { loadBudgetStatus, renderBudgetStatus } from './views/budget-status.js';
import { loadAutonomousOps, renderAutonomousOps } from './views/autonomous-ops.js';
import { loadAgencyFlow, renderAgencyFlow } from './views/agency-flow.js';
// Faz 38 PR-A — Workflow Tracer (Telegram komut → ajan zinciri).
import { loadWorkflowTrace, renderWorkflowTrace } from './views/workflow-trace.js';
// Faz 39 PR-A — Event Queue Health (SSE bridge altyapı dashboard panel).
import {
  loadEventQueue,
  renderEventQueue,
  startEventQueueTimer,
  stopEventQueueTimer,
  bindEventQueueRefresh,
} from './views/event-queue.js';
// Faz 44 PR-B — API Keys admin panel (3rd party API key yönetimi).
import { loadApiKeys, renderApiKeys, bindApiKeysDelegation } from './views/api-keys.js';
// Sprint 1 (Castagent Central Brain) — Plan §13 6 yeni view.
import { loadCentralBrain, renderCentralBrain } from './views/central-brain.js';
import { loadGenelBakis, renderGenelBakis } from './views/genel-bakis.js';
import {
  loadGovernanceSummary,
  renderGovernanceSummary,
  bindGovernanceSummaryDelegation,
} from './views/governance-summary.js';
import { renderHermesChat } from './views/hermes-chat.js';
import { loadClientOs, renderClientOs } from './views/client-os.js';
import { loadMusteriPortallari, renderMusteriPortallari } from './views/musteri-portallari.js';
import { loadNexus, renderNexus } from './views/nexus.js';
import { loadWhatsappDemo, renderWhatsappDemo } from './views/whatsapp-demo.js';
import { loadEventInbox, renderEventInbox } from './views/event-inbox.js';
import { loadWikiFreshness, renderWikiFreshness } from './views/wiki-freshness.js';
import { loadAgentSkills, renderAgentSkills } from './views/agent-skills.js';
import { loadAgentCouncil, renderAgentCouncil } from './views/agent-council.js';
// Sprint 2 PR-D2 — Central Brain operational views (read-only).
import { loadCbActions, renderCbActions } from './views/cb-actions.js';
import { loadCbApprovals, renderCbApprovals } from './views/cb-approvals.js';
import { loadCbLeadAnalyses, renderCbLeadAnalyses } from './views/cb-lead-analyses.js';
import { loadCbConversations, renderCbConversations } from './views/cb-conversations.js';
import { loadCbQuoteDrafts, renderCbQuoteDrafts } from './views/cb-quote-drafts.js';
// Twenty CRM PoC (T9) — read-only cockpit view.
import { loadCrmPoc, renderCrmPoc } from './views/crm-poc.js';
import { loadCrmObjects, renderCrmObjects } from './views/crm-objects.js';
import { loadAktifIsler, renderAktifIsler } from './views/aktif-isler.js';
// Aşama F (2026-05-25) — Yönetim görünürlük katmanı (Board + Org Chart + EFQM).
import { loadOrgChart, renderOrgChart } from './views/org-chart.js';
import { loadBoard, renderBoard } from './views/board.js';
import { loadEfqm, renderEfqm } from './views/efqm.js';
import { loadSatisfaction, renderSatisfaction } from './views/satisfaction.js';
import { loadHoldingKpi, renderHoldingKpi } from './views/holding-kpi.js';
// Aşama I4 (2026-05-29) — Castdesign Design Jobs listesi (SDK köprüsü I1).
import { loadDesignJobs, renderDesignJobs } from './views/design-jobs.js';
import { loadCredentialsExpiry, renderCredentialsExpiry, bindCredentialsExpiryRefresh } from './views/credentials-expiry.js';
import { loadMeetings, renderMeetings, startMeetingsPolling } from './views/meetings.js';
import { loadCeoCockpit, renderCeoCockpit } from './views/ceo-cockpit.js';
import { bindConversationDrawerEsc } from './ui/conversation-drawer.js';
import { renderSidebar, bindStaticNav, bindSidebarDelegation } from './ui/sidebar.js';
import { bindSidebarSearch } from './ui/sidebar-search.js';
import { renderStrip, renderLiveIndicator, startCountdown, bindTopbarRefresh, bindAgentStripDelegation } from './ui/topbar.js';
import { renderSuggestions, bindSuggestionsDelegation } from './ui/suggestions.js';
import { bindKbdToggle } from './ui/keyboard.js';
import { bindInfoDrawerStatic, bindDrawerBodyDelegation } from './ui/drawer.js';
import { bindDetailPanelStatic } from './ui/detail-panel.js';
import { connectSse } from './sse.js';
import type { PageId } from '../types/state';

// ── Devtools convenience: state + Api globalde ──────────────────────
// Modüller bunlari direct import yapiyor; sadece browser console debug için.
window.Api = Api;
window.state = state;

// ── guardAuth: Oturum yoksa login.html'e yonlendir ──────────────────

async function guardAuth() {
  try {
    const r = await Api.authMe();
    if (r.status === 401) {
      window.location.replace('/login.html');
      return false;
    }
    return true;
  } catch {
    window.location.replace('/login.html');
    return false;
  }
}

// ── navigate: Page change handler (sidebar tiklamalari) ─────────────

export function navigate(page: string) {
  state.currentPage = page as PageId;
  ['genel-bakis', 'client-os', 'nexus', 'hermes', 'pano', 'isler', 'ekip', 'canli', 'onaylar', 'beni-bekleyenler', 'skills', 'telegram', 'saglik', 'musteriler', 'mulkler', 'faturalar', 'kararlar', 'runtime-health', 'deploy-history', 'pilot-deploy-status', 'budget-status', 'agency-flow', 'workflow-trace', 'event-queue', 'api-keys', 'central-brain', 'whatsapp-demo', 'event-inbox', 'wiki-freshness', 'agent-skills', 'agent-council', 'cb-actions', 'cb-approvals', 'cb-lead-analyses', 'cb-conversations', 'cb-quote-drafts', 'org-chart', 'board', 'efqm', 'meetings', 'satisfaction', 'holding-kpi', 'credentials-expiry', 'design-jobs', 'autonomous-ops', 'crm-poc', 'crm-objects', 'aktif-isler', 'ceo-cockpit', 'musteri-portallari'].forEach(p => {
    const nav = document.getElementById('nav-' + p);
    const view = document.getElementById('view-' + p);
    if (nav) nav.className = 'sb-item' + (p === page ? ' active' : '');
    if (view) view.className = 'view' + (p === page ? ' active' : '');
  });
  // Client OS izole shell (co-mode): tenant panelinde iç sidebar/ajan-şeridi/iç-topbar gizlenir.
  document.body.classList.toggle('co-mode', page === 'client-os');
  const coBack = document.getElementById('co-back');
  if (coBack && !coBack.dataset.bound) { coBack.dataset.bound = '1'; coBack.addEventListener('click', () => navigate('genel-bakis')); }
  if (page === 'telegram' && !state.telegramSessions.length) loadTelegramSessions();
  if (page === 'saglik') {
    loadHealth();
    loadAudit();
    startSaglikTimer();
  } else {
    stopSaglikTimer();
  }
  // Faz 21 PR-B — Müşteriler view'a geçişte ilk yüklemeyi tetikle.
  if (page === 'musteriler') loadMusteriler();
  // Faz 46.E — Mülkler view'a geçişte vault/products/ fetch.
  if (page === 'mulkler') loadMulkler();
  // Faz 46.F.2 — İşler (Paperclip embed) view'a geçişte iframe lazy-load.
  if (page === 'isler') loadIsler();
  // Faz 22 PR-B — Faturalar view'a geçişte ilk yüklemeyi tetikle.
  if (page === 'faturalar') loadFaturalar();
  // Faz 22 PR-C — Kararlar view'a geçişte ilk yüklemeyi tetikle.
  if (page === 'kararlar') loadKararlar();
  // Faz 23 PR-B — Skills view'a geçişte usage telemetry fetch (catalog
  // loadMeta zaten refresh()'te çağrılıyor; usage ayrı endpoint).
  if (page === 'skills') loadSkillUsage();
  // Faz 28 PR-B — Runtime health view geçiş + auto-refresh timer
  if (page === 'runtime-health') {
    loadRuntimeHealth();
    startRuntimeHealthTimer();
  } else {
    stopRuntimeHealthTimer();
  }
  // Faz 30 — 6 yeni panel için lazy fetch
  if (page === 'deploy-history') loadDeployHistory();
  if (page === 'pilot-deploy-status') loadPilotDeployStatus();
  if (page === 'budget-status') loadBudgetStatus();
  if (page === 'autonomous-ops') loadAutonomousOps();
  if (page === 'agency-flow') loadAgencyFlow();
  if (page === 'workflow-trace') loadWorkflowTrace();
  // Faz 39 PR-A — Event Queue Health view geçiş + 30s auto-refresh timer.
  if (page === 'event-queue') {
    loadEventQueue();
    startEventQueueTimer();
  } else {
    stopEventQueueTimer();
  }
  // Faz 44 PR-B — API Keys admin paneli.
  if (page === 'api-keys') loadApiKeys();
  // Sprint 1 (Central Brain) — Plan §13 6 yeni view lazy load.
  // FIX (dashboard overhaul Tranche 2): async loadX() fire-and-forget ediliyordu;
  // navigate render()'ı HEMEN (loading state'inde) çiziyor, load bitince DOM güncellenmiyordu
  // → CB ailesi + yönetim view'ları sonsuza dek "Yükleniyor…"da asılı kalıyordu.
  // Her async load'a .then(render) ekleyerek load tamamlanınca dispatcher yeniden render ediyor.
  if (page === 'genel-bakis') {
    void loadGenelBakis().then(render);
    // Governance özet kartı paralel yüklenir; slot Genel Bakış html'i içinde.
    void loadGovernanceSummary().then(renderGovernanceSummary);
  }
  if (page === 'client-os') void loadClientOs().then(render);
  if (page === 'musteri-portallari') void loadMusteriPortallari().then(render);
  if (page === 'nexus') void loadNexus().then(render);
  if (page === 'central-brain') void loadCentralBrain().then(render);
  if (page === 'whatsapp-demo') loadWhatsappDemo();
  if (page === 'event-inbox') void loadEventInbox().then(render);
  if (page === 'wiki-freshness') void loadWikiFreshness().then(render);
  if (page === 'agent-skills') void loadAgentSkills().then(render);
  if (page === 'agent-council') void loadAgentCouncil().then(render);
  // Sprint 2 PR-D2 — Central Brain operational views.
  if (page === 'cb-actions') void loadCbActions().then(render);
  if (page === 'cb-approvals') void loadCbApprovals().then(render);
  if (page === 'cb-lead-analyses') void loadCbLeadAnalyses().then(render);
  if (page === 'cb-conversations') void loadCbConversations().then(render);
  if (page === 'cb-quote-drafts') void loadCbQuoteDrafts().then(render);
  if (page === 'crm-poc') void loadCrmPoc().then(render);
  if (page === 'crm-objects') void loadCrmObjects().then(render);
  if (page === 'aktif-isler') void loadAktifIsler().then(render);
  if (page === 'ceo-cockpit') void loadCeoCockpit().then(render);
  // Aşama F — Yönetim görünürlük katmanı
  if (page === 'org-chart') void loadOrgChart().then(render);
  if (page === 'board') void loadBoard().then(render);
  if (page === 'efqm') void loadEfqm().then(render);
  if (page === 'satisfaction') void loadSatisfaction().then(render);
  if (page === 'holding-kpi') void loadHoldingKpi().then(render);
  if (page === 'credentials-expiry') void loadCredentialsExpiry().then(render);
  if (page === 'meetings') void loadMeetings().then(render);
  // Aşama I4 — Castdesign Design Jobs lazy load.
  if (page === 'design-jobs') void loadDesignJobs().then(render);
  render();
}

// ── render: dispatcher (currentPage'e gore view re-render) ──────────

export function render() {
  renderLiveIndicator();
  if (state.currentPage === 'genel-bakis') {
    renderGenelBakis();
    // Genel Bakış HTML'i her render'da yeniden çiziliyor; slot kaybolur,
    // tekrar mevcut state ile doldur.
    renderGovernanceSummary();
  } else if (state.currentPage === 'client-os') {
    renderClientOs();
  } else if (state.currentPage === 'musteri-portallari') {
    renderMusteriPortallari();
  } else if (state.currentPage === 'nexus') {
    renderNexus();
  } else if (state.currentPage === 'hermes') {
    renderHermesChat();
  } else if (state.currentPage === 'pano') {
    renderKanban();
    renderAssigneeFilter();
  } else if (state.currentPage === 'ekip') {
    renderEkip();
    renderAccountsCard();
    renderFallbackCard();
  } else if (state.currentPage === 'canli') {
    renderCanli();
  } else if (state.currentPage === 'onaylar') {
    renderOnaylar();
  } else if (state.currentPage === 'beni-bekleyenler') {
    renderBeniBekleyenler();
  } else if (state.currentPage === 'skills') {
    renderSkills();
  } else if (state.currentPage === 'telegram') {
    renderTelegram();
  } else if (state.currentPage === 'saglik') {
    renderHealth();
    renderAudit();
  } else if (state.currentPage === 'musteriler') {
    // Faz 21 PR-B — Müşteriler kanban (customer_lifecycle).
    renderMusteriler();
  } else if (state.currentPage === 'mulkler') {
    // Faz 46.E — Mülkler kart liste (vault/products/).
    renderMulkler();
  } else if (state.currentPage === 'isler') {
    // Faz 46.F.2 — İşler (Paperclip iframe embed).
    renderIsler();
  } else if (state.currentPage === 'faturalar') {
    // Faz 22 PR-B — Faturalar timeline kanban (invoice_history).
    renderFaturalar();
  } else if (state.currentPage === 'kararlar') {
    // Faz 22 PR-C — Kararlar timeline (vault decision files).
    renderKararlar();
  } else if (state.currentPage === 'runtime-health') {
    // Faz 28 PR-B — Runtime telemetri (issue throughput, stuck, heatmap, auth-fail).
    renderRuntimeHealth();
  } else if (state.currentPage === 'deploy-history') {
    renderDeployHistory();
  } else if (state.currentPage === 'pilot-deploy-status') {
    renderPilotDeployStatus();
  } else if (state.currentPage === 'budget-status') {
    renderBudgetStatus();
  } else if (state.currentPage === 'autonomous-ops') {
    renderAutonomousOps();
  } else if (state.currentPage === 'agency-flow') {
    renderAgencyFlow();
  } else if (state.currentPage === 'workflow-trace') {
    renderWorkflowTrace();
  } else if (state.currentPage === 'event-queue') {
    // Faz 39 PR-A — SSE Bridge altyapı: queue depth/processed/failed paneli.
    renderEventQueue();
  } else if (state.currentPage === 'api-keys') {
    // Faz 44 PR-B — 3rd party API key admin paneli.
    renderApiKeys();
  } else if (state.currentPage === 'central-brain') {
    renderCentralBrain();
  } else if (state.currentPage === 'whatsapp-demo') {
    renderWhatsappDemo();
  } else if (state.currentPage === 'event-inbox') {
    renderEventInbox();
  } else if (state.currentPage === 'wiki-freshness') {
    renderWikiFreshness();
  } else if (state.currentPage === 'agent-skills') {
    renderAgentSkills();
  } else if (state.currentPage === 'agent-council') {
    renderAgentCouncil();
  } else if (state.currentPage === 'cb-actions') {
    renderCbActions();
  } else if (state.currentPage === 'cb-approvals') {
    renderCbApprovals();
  } else if (state.currentPage === 'cb-lead-analyses') {
    renderCbLeadAnalyses();
  } else if (state.currentPage === 'cb-conversations') {
    renderCbConversations();
  } else if (state.currentPage === 'cb-quote-drafts') {
    renderCbQuoteDrafts();
  } else if (state.currentPage === 'crm-poc') {
    renderCrmPoc();
  } else if (state.currentPage === 'crm-objects') {
    renderCrmObjects();
  } else if (state.currentPage === 'aktif-isler') {
    renderAktifIsler();
  } else if (state.currentPage === 'ceo-cockpit') {
    renderCeoCockpit();
  } else if (state.currentPage === 'org-chart') {
    // Aşama F — Organizasyon şeması (CHARTER v1.3 hiyerarşi)
    renderOrgChart();
  } else if (state.currentPage === 'board') {
    // Aşama F — Yönetim kurulu görünümü
    renderBoard();
  } else if (state.currentPage === 'efqm') {
    // Aşama F — EFQM 5 kriter + RADAR
    renderEfqm();
  } else if (state.currentPage === 'satisfaction') {
    // Aşama G G5 — Müşteri memnuniyeti (NPS/CSAT/CES)
    renderSatisfaction();
  } else if (state.currentPage === 'holding-kpi') {
    // Aşama G G7 — Holding KPI çekirdeği
    renderHoldingKpi();
  } else if (state.currentPage === 'credentials-expiry') {
    // 2026-05-29 mimari denetim — yetim view wire edildi
    renderCredentialsExpiry();
  } else if (state.currentPage === 'meetings') {
    // Acil Toplantı Mekanizması (MVP 2026-05-27)
    renderMeetings();
  } else if (state.currentPage === 'design-jobs') {
    // Aşama I4 — Castdesign Design Jobs listesi
    renderDesignJobs();
  }
}

// ── fetchAll: Companies + agents + tasks + approvals + fallback ─────

// Son fetchAll'da sessizce boş diziye düşen per-company yükleme sayısı
// (refresh() topbar "Kısmi veri" uyarısı için okur).
let partialLoadFailures = 0;

async function fetchAll() {
  const ring = document.getElementById('refresh-ring');
  if (ring) ring.classList.add('spinning');
  try {
    // Faz 15: Api.getCompanies artik apiFetch kullaniyor → 401 redirect + 5xx
    // toast merkezi. Burada explicit `r.status === 401` check'i kaldirildi
    // (apiFetch redirect + throw yapar, asagidaki catch'e duser).
    // Faz F fix — companies endpoint 403/hata durumunda graceful degradation.
    // Paperclip board key /api/companies için yetki kısıtlı → 403 "Board access required".
    // Bu durumda state.companies = [] olsun; dashboard "Sistem bekliyor" göstersin.
    // Proxy pool tükenmesi → "fetch failed" durumunda da aynı davranış.
    let cosData: any = [];
    try {
      const cosRes = await Api.getCompanies();
      if (cosRes.ok) {
        const raw = await cosRes.json();
        cosData = Array.isArray(raw) ? raw : (raw?.companies ?? []);
      } else {
        console.warn('[dashboard] getCompanies HTTP', cosRes.status, '— boş devam');
      }
    } catch (cosErr) {
      console.warn('[dashboard] getCompanies başarısız, boş devam:', cosErr);
    }
    state.companies = Array.isArray(cosData) ? cosData : [];

    // Hata ≠ boş ayrımı: per-company yükleme hataları sessizce boş dizi olur
    // (board kısmen render edilebilsin diye) ama sayaçla toplanır; refresh()
    // topbar'da "Kısmi veri" uyarısı basar — boş board "veri yok" sanılmasın.
    let failedLoads = 0;
    const swallow = () => { failedLoads += 1; return [] as any[]; };
    const [agRaw, tkRaw, apRaw] = await Promise.all([
      Promise.all(state.companies.map(c =>
        Api.getCompanyAgents(c.id).then(r => r.json())
          .then((d: any) => (d.agents || d || []).map((a: any) => ({ ...a, company_name: c.name, company_id: c.id })))
          .catch(swallow)
      )),
      Promise.all(state.companies.map(c =>
        // Faz 52 — Initial fetch done hariç (Pano lazy load). 5 kolon
        // (backlog/todo/in_progress/blocked/in_review) ~250 issue. Done kolonu
        // user explicit tıklayınca getCompanyIssuesDone ile dolar.
        Api.getCompanyIssues(c.id, { excludeDone: true, limit: 500 }).then(r => r.json())
          .then((d: any) => (d.issues || d || []).map((t: any) => ({ ...t, company_name: c.name, company_id: c.id })))
          .catch(swallow)
      )),
      Promise.all(state.companies.map(c =>
        Api.getCompanyApprovals(c.id).then(r => r.json())
          .then((d: any) => (d.approvals || d || []).filter((a: any) => a.status === 'pending').map((a: any) => ({ ...a, companyId: c.id })))
          .catch(swallow)
      )),
    ]);
    partialLoadFailures = failedLoads;

    state.agents = agRaw.flat();
    state.tasks = tkRaw.flat().sort(compareTasksStable);
    state.approvals = apRaw.flat();

    // Faz 50 M6 — Central Brain actionable approvals merge.
    // Eski: state.approvals sadece paperclip.public.approvals (legacy, 0 pending).
    // Yeni: central_brain.approvals (P1 executor uyumlu, status=pending +
    // executor_status IN NULL/pending) eklenir. Onaylar widget + sidebar badge +
    // suggestions panel artık cb-approvals üst banner ile aynı kaynak kullanır
    // ("0/43 pending" → "Bekleyen onay yok" tutarsızlığı giderildi).
    try {
      const cbRes = await Api.getCbActionableApprovals();
      if (cbRes.ok) {
        const cbData = await cbRes.json();
        // Defensive filter (2026-05-15 21:15) — backend `actionableOnly=1` filter
        // bazen approved/rejected kayıtları döndürüyor (M6 follow-up incident).
        // Frontend hardcoded `status:'pending'` etiketi 7 kart gösteriyordu, 3'ü
        // gerçek approved/rejected. Burada status + executor_status defensive
        // filter yapılır; backend fix Faz 49+ ayrı PR.
        const cbList: any[] = (cbData.approvals || [])
          .filter((a: any) => a.status === 'pending'
            && (a.executorStatus == null || a.executorStatus === 'pending'))
          .map((a: any) => ({
            id: a.id,
            status: a.status, // backend value (defensive filter sonrası garanti pending)
            type: a.approvalType,
            companyId: a.tenantId,
            executorStatus: a.executorStatus,
            payload: {
              title: a.action?.title || a.approvalType,
              description: a.reason || '',
            },
            _source: 'central_brain',
          }));
        state.approvals = state.approvals.concat(cbList);
      }
    } catch (_e) {
      // central_brain proxy henüz yok / yetkisiz: paperclip approvals'la devam.
    }

    // UWQ v2 — Aktif İşler sidebar rozeti: açılışta fire-and-forget doldur
    // (view'a girmeden Yılmaz-bekleyen sayısı görünsün; loadAktifIsler rozeti de günceller).
    void loadAktifIsler().then(() => {
      if (state.currentPage === 'aktif-isler') renderAktifIsler();
    }).catch(() => {});

    // Fallback stats — fire-and-forget, ekip viewdeyse re-render.
    Api.getFallbackStats().then(r => r.ok ? r.json() : null).then(d => {
      if (d?.stats) {
        state.fallbackStats = d.stats;
        if (state.currentPage === 'ekip') renderFallbackCard();
      }
    }).catch(() => {});

    // Accounts card (ekip view'inda gorunur).
    fetchAccounts();

    return true;
  } catch (e) {
    console.error(e);
    return false;
  } finally {
    if (ring) ring.classList.remove('spinning');
  }
}

// ── refresh: fetchAll + render + countdown reset ────────────────────

export async function refresh() {
  const ok = await fetchAll();
  if (ok) {
    renderSidebar();
    renderStrip();
    renderSuggestions();
    render();
    const lu = document.getElementById('last-update');
    if (lu) {
      lu.textContent = 'Güncellendi ' +
        new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    }
    // Kısmi başarısızlık görünür olsun: boş kolon "veri yok" sanılmasın.
    const ll = document.getElementById('live-label');
    if (ll) {
      ll.textContent = partialLoadFailures > 0
        ? `Kısmi veri — ${partialLoadFailures} yükleme başarısız`
        : 'Canlı';
    }
  } else {
    const ll = document.getElementById('live-label');
    if (ll) ll.textContent = 'Bağlantı hatası';
  }
  startCountdown();
  loadMeta(); // 60s TTL'li, fire-and-forget (skills + agents/full meta cache)
}

// Faz 14 PR-B: navigate/render/refresh artik named export — direct import.
// guardAuth/fetchAll hala internal (main.ts içinde kullanılıyor).

// ── Bootstrap ───────────────────────────────────────────────────────
// DOMContentLoaded sonrasinda calistir. type="module" defer-by-default oldugu
// icin tum import'lar bu noktada zaten resolve, window expose'lar tamam.

// ── Bundle Staleness Watchdog (2026-05-28) ─────────────────────────────
// Sorun: Service Worker eski bundle cache'liyor → drawer poll fail → UI donuk
// (Zümrüt timeout sorunu, 2026-05-28 yönetim kurulu toplantısı).
//
// Çözüm: 3 katmanlı invalidation
//   1. Boot'ta SW.update() force → browser yeni sw.js indirir, eski cache silinir
//   2. 5sn + her 60sn manifest.json hash check → mismatch → unregister + reload
//   3. Visibility change (sekme aktif olunca) tekrar check
//
// Sessiz fail (try/catch) — boot bloklamaz.
async function bundleStalenessCheck(): Promise<boolean> {
  try {
    // 1. SW update force
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) await reg.update().catch(() => {});
    }
    // 2. Manifest hash check (cache by-pass)
    const resp = await fetch('/dist/manifest.json?t=' + Date.now(), { cache: 'no-store' });
    if (!resp.ok) return false;
    const manifest: { [key: string]: string } = await resp.json();
    const liveBundle = manifest['main.js'] || '';
    if (!liveBundle) return false;
    // Mevcut yüklenmiş bundle src'sini bul
    const scriptEl = document.querySelector<HTMLScriptElement>('script[src*="/dist/main."]');
    const currentSrc = scriptEl?.getAttribute('src') || '';
    if (currentSrc && !currentSrc.includes(liveBundle)) {
      console.warn('[bundle-staleness] mismatch!', { current: currentSrc, live: liveBundle });
      // 3. SW unregister + cache clear
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        for (const reg of regs) await reg.unregister().catch(() => {});
      }
      if ('caches' in self) {
        const keys = await caches.keys();
        await Promise.all(keys.map(k => caches.delete(k).catch(() => false)));
      }
      console.warn('[bundle-staleness] cleared, reloading...');
      location.reload();
      return true;
    }
    return false;
  } catch {
    // Sessiz fail — bridge etmeye değmez
    return false;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  // Bundle staleness watchdog — 5sn sonra ilk check, sonra her 60sn
  setTimeout(() => { void bundleStalenessCheck(); }, 5000);
  setInterval(() => { void bundleStalenessCheck(); }, 60000);
  // Sekme aktif olunca da kontrol (uyandığında stale tespit)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void bundleStalenessCheck();
  });

  guardAuth().then(ok => {
    if (!ok) return;
    // Faz 10 PR1 — statik HTML element'lere addEventListener bind
    // (inline onclick handler'lari silindi, listener'lar burada bind edilir).
    bindStaticNav();
    bindSidebarSearch();  // F4-B+ (2026-05-26) — sidebar arama kutusu (40 view filtreleme)
    bindTopbarRefresh();
    bindKbdToggle();
    // Faz 10 PR2 — modal + detail panel statik handler bind (17 inline silindi)
    bindInfoDrawerStatic();
    // Sprint 2 PR-D2 — Conversation drawer ESC keyhandler (global, bir kez bind).
    bindConversationDrawerEsc();
    bindDetailPanelStatic();
    bindNewIssueModalStatic();
    bindTelegramSendStatic();
    // Faz 10 PR3 — Pano statik handler'lar (suggestions toggle, new issue,
    // 4 filter input, clear) + #kanban-board issueCard click delegation
    bindPanoStatic();
    bindKanbanDelegation();
    // Faz 10 PR4 — Onaylar + Skills + Ekip dynamic delegated listeners
    bindOnaylarDelegation();
    bindSkillsDelegation();
    bindEkipDelegation();
    // Faz 10 PR5 — Sidebar companies + Topbar agent strip delegation
    bindSidebarDelegation();
    bindAgentStripDelegation();
    // Faz 10 PR6 — Saglik static + audit feed + Telegram view + Drawer body
    bindSaglikStatic();
    bindAuditFeedDelegation();
    bindTelegramRefresh();
    bindTelegramGridDelegation();
    bindDrawerBodyDelegation();
    // Faz 10 PR7 — Suggestions delegated (XSS fix: fn.toString() yerine action cache)
    bindSuggestionsDelegation();
    // Faz 28 PR-B — Runtime health view refresh button
    bindRuntimeHealthRefresh();
    // Faz 39 PR-A — Event Queue Health view refresh button
    bindEventQueueRefresh();
    // Faz 30 — Hotfix + Auto-block panel delegation (admin-bearer butonlari)
    // Faz 44 PR-B — API Keys panel delegation (CRUD butonlari)
    bindApiKeysDelegation();
    // EFQM Kriteri 4 — Genel Bakış governance kartındaki "Tüm kararlar →"
    // linkinden Agent Council view'ına programatik navigation.
    bindGovernanceSummaryDelegation();
    // Aşama F (2026-05-25) — Yönetim görünüm yenile butonları
    const _orgRefreshBtn = document.getElementById('org-chart-refresh-btn');
    if (_orgRefreshBtn) _orgRefreshBtn.addEventListener('click', () => void loadOrgChart());
    const _boardRefreshBtn = document.getElementById('board-refresh-btn');
    if (_boardRefreshBtn) _boardRefreshBtn.addEventListener('click', () => void loadBoard());
    const _efqmRefreshBtn = document.getElementById('efqm-refresh-btn');
    if (_efqmRefreshBtn) _efqmRefreshBtn.addEventListener('click', () => void loadEfqm());
    const _satRefreshBtn = document.getElementById('satisfaction-refresh-btn');
    if (_satRefreshBtn) _satRefreshBtn.addEventListener('click', () => void loadSatisfaction());
    const _kpiRefreshBtn = document.getElementById('holding-kpi-refresh-btn');
    if (_kpiRefreshBtn) _kpiRefreshBtn.addEventListener('click', () => void loadHoldingKpi());
    // Aşama I4 — Castdesign Design Jobs yenile butonu
    const _djRefreshBtn = document.getElementById('design-jobs-refresh-btn');
    if (_djRefreshBtn) _djRefreshBtn.addEventListener('click', () => void loadDesignJobs());
    bindCredentialsExpiryRefresh();
    // K7 — portal derin-linki: Müşteri Portalları listesindeki "Aç" `?tenant=<slug>` üretir.
    // Parametre varsa doğrudan Client OS'a git; yoksa varsayılan sayfa korunur. Böylece
    // bir portalı açmak için slug'ı ezbere bilmek gerekmiyor.
    try {
      if (new URLSearchParams(location.search).get('tenant')) navigate('client-os');
    } catch { /* URL parse edilemezse varsayılan sayfa */ }
    // Faz 52-Y W3a — varsayılan açılış "Bugün" (ceo-cockpit): navigate() ile
    // gelinmediği için lazy-load burada tetiklenir (refresh() cockpit yüklemez).
    if (state.currentPage === 'ceo-cockpit') void loadCeoCockpit().then(render);
    // Acil Toplantı polling (5sn, sadece view açık veya aktif toplantı varsa)
    startMeetingsPolling();
    refresh();
    connectSse();
    // Faz 46.F.3 — Topbar Sağlık Özeti pills (issues + agents canlı sayılar, 30s).
    startHealthSummaryTimer();
    // Aşama D2 (2026-05-24) — MicroFX micro-interactions auto-bind.
    // data-reveal / data-hover-lift / data-tap-feedback / data-focus-ring
    // attribute'ları olan element'leri tek tek bağlar. prefers-reduced-motion
    // saygısı her fn içinde. Boot log _mfxStats.
    import('./ui/microfx.js').then((mfx) => {
      const _mfxStats = mfx.initMicroFX();
      // Debug: konsola sayım yaz (production'da silinebilir)
      if (typeof console !== 'undefined' && console.debug) {
        console.debug('[MicroFX] bound:', _mfxStats);
      }
    }).catch((err) => {
      console.warn('[MicroFX] init failed:', err);
    });
  });
});
